// src/utils/clientConnectorUtils.ts

import dayjs from 'dayjs';

export interface TimestampLabel {
    label: string;
    value: string | Date | undefined;
}

export const getLatestTimestamp = (log?: {
    CompletedAtUtc?: string | Date;
    AutomaticallyTriggeredAtUtc?: string | Date;
    ManuallyTriggeredAtUtc?: string | Date;
}): TimestampLabel | undefined => {
    return [
        { label: 'Completed', value: log?.CompletedAtUtc },
        { label: 'Automatically Triggered', value: log?.AutomaticallyTriggeredAtUtc },
        { label: 'Manually Triggered', value: log?.ManuallyTriggeredAtUtc },
    ]
        .filter(t => t.value)
        .sort((a, b) => dayjs(b.value).valueOf() - dayjs(a.value).valueOf())[0];
};

export const getLatestTimestampExpiry = (
    latestTimestamp: TimestampLabel | undefined,
    minutes: number = 30
): Date => {
    if (!latestTimestamp?.value) return new Date(0); // fallback to Epoch
    return new Date(dayjs(latestTimestamp.value).add(minutes, 'minute').toISOString());
};

export const isRefreshing = (
    log: {
        CompletedAtUtc?: string | Date;
        AutomaticallyTriggeredAtUtc?: string | Date;
        ManuallyTriggeredAtUtc?: string | Date;
    },
    expiryMinutes: number = 30
): boolean => {
    const latest = getLatestTimestamp(log);
    const expiry = getLatestTimestampExpiry(latest, expiryMinutes);
    return !log?.CompletedAtUtc && expiry >= new Date();
};

export const hasWarning = (
    log: {
        CompletedAtUtc?: string | Date;
        AutomaticallyTriggeredAtUtc?: string | Date;
        ManuallyTriggeredAtUtc?: string | Date;
    },
    minutes: number = 30
): boolean => {
    const latest = getLatestTimestamp(log);
    const expiry = getLatestTimestampExpiry(latest, minutes);
    return !log?.CompletedAtUtc && expiry < new Date();
};

export const getStatusTextFromLog = (
    log: {
        CompletedAtUtc?: string | Date;
        AutomaticallyTriggeredAtUtc?: string | Date;
        ManuallyTriggeredAtUtc?: string | Date;
    }
): string => {
    const latest = getLatestTimestamp(log);
    if (!latest?.value) return 'Never started';
    return latest.label === 'Completed' ? 'Completed' : 'Started';
};

export const getStatusExplanation = (
    log: {
        CompletedAtUtc?: string | Date;
        AutomaticallyTriggeredAtUtc?: string | Date;
        ManuallyTriggeredAtUtc?: string | Date;
        HasError?: boolean;
    },
    status: string | undefined,
    minutes: number = 30
): string => {
    if (['DISCONNECTED', 'DELETED'].includes(status ?? '')) return '';
    if (hasWarning(log, minutes) && !log?.HasError) return 'Taking longer than expected.';
    if (!hasWarning(log, minutes) && log?.HasError) return 'Encountered an error.';
    if (isRefreshing(log, minutes)) return 'Currently refreshing...';
    if (log?.CompletedAtUtc) return 'Connector is up to date.';
    return 'Pending or unknown status.';
};

export const allowSynchronisation = (
    log: {
        CompletedAtUtc?: string | Date;
        AutomaticallyTriggeredAtUtc?: string | Date;
        ManuallyTriggeredAtUtc?: string | Date;
    },
    minutes: number = 30
): boolean => {
    const latest = getLatestTimestamp(log);
    const expiry = getLatestTimestampExpiry(latest, minutes);
    return !log || !!log.CompletedAtUtc || expiry < new Date();
};

export const truncateText = (text: string, maxLength: number): string =>
    !text ? 'No detail' : text.length > maxLength ? text.slice(0, maxLength) + '...' : text;

export const isTruncated = (text: string, maxLength: number): boolean =>
    !!text && text.length > maxLength;

export interface LogType {
    CompletedAtUtc?: string | Date;
    AutomaticallyTriggeredAtUtc?: string | Date;
    ManuallyTriggeredAtUtc?: string | Date;
    HasError?: boolean;
}

export const getStatusIcon = (log: LogType, status: string): 'warning' | 'error' | 'schedule' | 'check_circle' | 'pending' | 'delete' | 'link_off' | undefined => {
    if (status === 'DELETED') return 'delete';
    if (status === 'DISCONNECTED') return 'link_off';
    if (hasWarning(log) && !log?.HasError) return 'warning';
    if (!hasWarning(log) && log?.HasError) return 'error';
    if (log && !log?.CompletedAtUtc) return 'schedule';
    if (log?.CompletedAtUtc) return 'check_circle';
    return 'pending';
};